import {
  IAiAgentModel,
  ICrewAgent,
  ICrewCustomDataType,
  ICrewInput,
  ICrewManagerAgent,
  ITestAgentResponse
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { createRef, PureComponent, ReactNode } from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./ai-agent-modals.scss";

const TYPE_OPTIONS = [
  { value: "str", label: "string" },
  { value: "bool", label: "boolean" },
  { value: "float", label: "float" },
  { value: "integer", label: "integer" }
];

export interface IAiAgentTestModalProps {
  isOpen: boolean;
  inputs: ICrewInput[];
  agents: ICrewAgent[];
  customDataTypes?: ICrewCustomDataType[];
  processType?: number;
  managerAgent?: ICrewManagerAgent;
  projectId: string;
  actionName: string;
  onClose: () => void;
}

export interface IAiAgentTestModalState {
  isLoading?: boolean;
  /** Where Test stands. Owned by `SaveStatus`, never set directly. */
  testStatus: KuikaButtonStatus;
  testResults?: ITestAgentResponse;
  showResults?: boolean;
  error?: string;
  isFormValid?: boolean;
  agentModels?: IAiAgentModel[];
}

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class AiAgentTestModal extends PureComponent<IAiAgentTestModalProps, IAiAgentTestModalState> {
  formRef = createRef<FormInstance<any>>();

  private readonly testStatus: SaveStatus = new SaveStatus((testStatus: KuikaButtonStatus): void => {
    this.setState({ testStatus });
  });

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      testStatus: "idle",
      testResults: undefined,
      showResults: false,
      isFormValid: false
    };
  }

  componentWillUnmount(): void {
    this.testStatus.dispose();
  }

  componentDidUpdate(
    prevProps: Readonly<IAiAgentTestModalProps>,
    prevState: Readonly<IAiAgentTestModalState>,
    snapshot?: any
  ): void {
    if (prevProps.isOpen === false && this.props.isOpen === true) {
      this.handleLoad();
    }
  }

  handleLoad = () => {
    // Reset form and results when modal opens
    this.formRef.current?.resetFields();
    this.setState({
      testResults: undefined,
      showResults: false,
      error: undefined,
      isFormValid: false
    });
    this.testStatus.reset();
    this.loadAgentModels();
  };

  loadAgentModels = async () => {
    try {
      const response = await DatasourceService.GetAiModels();
      if (response?.data) {
        this.setState({ agentModels: response.data });
      }
    } catch (error) {
      console.error("Failed to load agent models for test modal:", error);
    }
  };

  handleFormChange = () => {
    // Check if all required fields are filled
    const fields = this.formRef.current?.getFieldsValue();
    const errors = this.formRef.current?.getFieldsError();

    if (!fields || !errors) {
      this.setState({ isFormValid: false });
      return;
    }

    // Check if there are any errors
    const hasErrors = errors.some(({ errors }) => errors.length > 0);

    // Check if all required fields have values
    const allFieldsFilled = this.props.inputs.every((input) => {
      const value = fields[input.parameter_name];
      return value !== undefined && value !== null && value !== "";
    });

    this.setState({ isFormValid: !hasErrors && allFieldsFilled });
  };

  closeModal = (): void => {
    if (this.state.isLoading) return;
    this.props.onClose();
  };

  private extractErrorMessage(error: any): string {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data) {
      try {
        return typeof error.response.data === "string" ? error.response.data : JSON.stringify(error.response.data);
      } catch {
        return "An error occurred while testing the agent.";
      }
    }
    if (error?.message) {
      return error.message;
    }
    return "Failed to test agent. Please try again.";
  }

  private parseAgentError(rawMessage: string): string {
    // Define error patterns with their corresponding user-friendly messages
    // Patterns are checked in order - first match wins
    const errorPatterns = [
      // Backend validation errors - OpenAI (pass through as-is - already user-friendly)
      {
        patterns: [
          "OpenAI API key is missing",
          "OpenAI API key validation failed",
          "Failed to validate OpenAI API key"
        ],
        message: rawMessage // Pass through
      },
      // Backend validation errors - Serper (pass through as-is - already user-friendly)
      {
        patterns: [
          "Serper API key is required",
          "Serper API key validation failed",
          "Failed to validate Serper API key"
        ],
        message: rawMessage // Pass through
      },
      // Backend validation - Serper insufficient credits (specific pattern)
      {
        patterns: ["insufficient credits", "The Serper account"],
        message: "The Serper account associated with the the provided API key has insufficient credits."
      },
      // Backend validation - Serper invalid key (specific pattern)
      {
        patterns: ["Provided Serper key is not valid"],
        message: "Serper API key is invalid. Please verify your API key in App Settings."
      },
      // Python service OpenAI errors
      {
        patterns: [
          "You didn't provide an API key",
          "Error code: 401",
          "OPENAI_API_KEY is required",
          "Error importing native provider: OPENAI_API_KEY"
        ],
        message: "OpenAI API key is missing or invalid. Please configure your API key in App Settings."
      },
      {
        patterns: ["insufficient_quota", "quota exceeded", "Error code: 429"],
        message: "OpenAI API quota exceeded. Please check your OpenAI account credits and billing."
      },
      {
        patterns: ["rate_limit_exceeded"],
        message: "OpenAI rate limit exceeded. Please wait a moment and try again."
      },
      // Python service Serper errors
      {
        patterns: ["SERPER_API_KEY is required"],
        message: "Serper API key is missing. Please configure your Serper API key in App Settings."
      },
      {
        patterns: ["serper"],
        additionalCheck: (msg: string) =>
          msg.toLowerCase().includes("quota") ||
          msg.toLowerCase().includes("limit") ||
          msg.toLowerCase().includes("credit"),
        message: "Serper API quota exceeded or rate limited. Please check your Serper account."
      },
      // Code generation exceptions
      {
        patterns: ["Code generation exception"],
        message: (msg: string) => {
          const errorMatch = msg.match(/Error Message:\s*\n\n([^\n]+(?:\n(?!\s+at\s)[^\n]+)*)/);
          if (errorMatch && errorMatch[1]) {
            return errorMatch[1].trim();
          }
          return msg.replace(/\\"/g, '"').replace(/\\n/g, "\n");
        }
      }
    ];

    // Find first matching pattern
    for (const { patterns, message, additionalCheck } of errorPatterns) {
      const matchesPattern = patterns.some((pattern) => rawMessage.includes(pattern));
      const passesAdditionalCheck = !additionalCheck || additionalCheck(rawMessage);

      if (matchesPattern && passesAdditionalCheck) {
        return typeof message === "function" ? message(rawMessage) : message;
      }
    }

    // No pattern matched - return original message
    return rawMessage;
  }

  onTest = async (data: any) => {
    // The results replace the inputs after the ✓, so a second press during it would run the
    // agent again behind them.
    if (!this.testStatus.canStart) return;
    this.testStatus.start();
    this.setState({ error: undefined });

    try {
      const { agents, customDataTypes, projectId, actionName } = this.props;

      // Map form values to crew_inputs with parameter_default_value
      const crewInputsWithValues = this.props.inputs.map((input) => {
        let value = data[input.parameter_name];

        // Handle List[str] format - convert comma-separated string to JSON array
        if (input.parameter_format?.startsWith("List[")) {
          if (typeof value === "string") {
            // Split by comma, trim whitespace, filter empty values
            const items = value
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item.length > 0);
            value = JSON.stringify(items);
          }
        } else {
          value = value?.toString();
        }

        return {
          ...input,
          parameter_default_value: value
        };
      });

      // Normalize model field to { id: "<guid>" } for backend compatibility.
      // Accepts legacy shapes: plain Guid string, model-name string, or { id, name } object.
      // Resolves name-or-id to a Guid via the loaded agent model list when needed.
      const { agentModels } = this.state;
      const resolveToGuid = (raw?: string): string | undefined => {
        if (!raw) return undefined;
        const trimmed = raw.trim();
        if (!trimmed) return undefined;
        if (GUID_REGEX.test(trimmed)) return trimmed;
        if (!agentModels?.length) return undefined;
        const lower = trimmed.toLowerCase();
        const byId = agentModels.find((m) => m.id.toLowerCase() === lower);
        if (byId) return byId.id;
        const byName = agentModels.find((m) => m.name.toLowerCase() === lower);
        return byName?.id;
      };
      const serializeModel = (model?: string | { id?: string; name?: string }) => {
        if (!model) return undefined;
        const candidate = typeof model === "string" ? model : model.id || model.name;
        const guid = resolveToGuid(candidate);
        return guid ? { id: guid } : undefined;
      };

      const serializedAgents = agents.map((a) => ({
        ...a,
        model: serializeModel(a.model as string | { id?: string; name?: string }),
        functionCallingLlm: serializeModel(a.functionCallingLlm as string | { id?: string; name?: string } | undefined)
      }));

      // Build the AI Agent Info payload
      const aiAgentInfo: Record<string, unknown> = {
        crewAgents: serializedAgents,
        crew_inputs: crewInputsWithValues,
        crew_custom_data_types: customDataTypes
      };

      if (this.props.processType != null) {
        aiAgentInfo.processType = this.props.processType;
      }
      if (this.props.managerAgent) {
        aiAgentInfo.managerAgent = {
          ...this.props.managerAgent,
          model: serializeModel(this.props.managerAgent.model as string | { id?: string; name?: string }),
          functionCallingLlm: serializeModel(this.props.managerAgent.functionCallingLlm)
        };
      }

      // Call the generateaiagentproject endpoint
      const response = await DatasourceService.TestAiAgent(projectId, actionName, aiAgentInfo);

      console.log("TestAiAgent response:", response);

      // Check if response contains an error (even with 200 status)
      if (response.data && typeof response.data === "object" && "error" in response.data) {
        throw new Error(response.data.error || "Agent test returned an error");
      }

      if (
        response.data &&
        typeof response.data === "object" &&
        "message" in response.data &&
        response.data.message.includes("exception")
      ) {
        throw new Error(response.data.message);
      }

      if (response.data) {
        this.setState({ testResults: response.data, error: undefined });
        // Test says it is done first; the results replace the inputs after the hold, the same
        // order every other commit keeps (✓, then the surface moves on).
        this.testStatus.succeed((): void => {
          this.setState({ showResults: true });
        });
      } else {
        throw new Error("No response data received from agent test");
      }
    } catch (error: any) {
      console.error("Agent test failed:", error);

      const rawMessage = this.extractErrorMessage(error);
      const errorMessage = this.parseAgentError(rawMessage);

      // The inputs stay, with the reason in the banner and "Try again" on the button.
      this.testStatus.fail();
      this.setState({
        error: errorMessage,
        showResults: false
      });
    }
  };

  renderInputsForm = () => {
    const { inputs } = this.props;
    const { error } = this.state;

    return (
      <>
        {error && (
          <div className="kuika-ai-agent-test-modal__error">
            <KuikaTextMedium className="kuika-ai-agent-test-modal__error-text">{error}</KuikaTextMedium>
          </div>
        )}
        {!inputs || inputs.length === 0 ? (
          <KuikaEmptyState
            svgName="field"
            svgType="common"
            name="agent inputs"
            pastTense="configured"
            description="You can still test if your agent doesn't require any inputs."
          />
        ) : (
          <div className="kuika-ai-agent-test-modal__inputs">
            {inputs.map((input, index) => (
              <div className="kuika-ai-agent-test-modal__input-field" key={index}>
                <label className="kuika-ai-agent-test-modal__input-label">{input.parameter_name}</label>
                <Form.Item name={input.parameter_name} rules={[{ required: true, message: "This field is required" }]}>
                  {input.parameter_format === "bool" ? (
                    <KuikaSelectbox
                      options={[
                        { value: "true", label: "True" },
                        { value: "false", label: "False" }
                      ]}
                      placeholder={`Enter ${input.parameter_name}`}
                      onChange={this.handleFormChange}
                    />
                  ) : (
                    <KuikaInput
                      type={
                        input.parameter_format === "integer" || input.parameter_format === "float" ? "number" : "text"
                      }
                      placeholder={
                        input.parameter_format?.startsWith("List[")
                          ? `Enter comma-separated values (e.g., value1, value2, value3)`
                          : `Enter ${input.parameter_name}`
                      }
                      onChange={this.handleFormChange}
                    />
                  )}
                </Form.Item>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  renderResults = () => {
    const { testResults } = this.state;
    const { agents } = this.props;

    if (!testResults) return null;

    return (
      <div className="kuika-ai-agent-test-modal__results">
        <div className="kuika-ai-agent-test-modal__results-header">
          <div className="kuika-ai-agent-test-modal__results-title">Test Results</div>
          <div className="kuika-ai-agent-test-modal__results-token">Token Usage: {testResults.token_usage}</div>
        </div>

        <div className="kuika-ai-agent-test-modal__results-grid">
          {testResults.tasks_output.map((task, index) => {
            // Find the corresponding agent to get the original task description
            const agent = agents[index];
            const taskDescription = agent?.task?.description || task.task_description;

            return (
              <div className="kuika-ai-agent-test-modal__result-card" key={index}>
                <div className="kuika-ai-agent-test-modal__result-card-header">
                  <span className="kuika-ai-agent-test-modal__result-card-agent">{task.agent}</span>
                </div>
                <div className="kuika-ai-agent-test-modal__result-card-body">
                  <div className="kuika-ai-agent-test-modal__result-field">
                    <div className="kuika-ai-agent-test-modal__result-field-label">Task Description</div>
                    <div className="kuika-ai-agent-test-modal__result-field-value">{taskDescription}</div>
                  </div>
                  <div className="kuika-ai-agent-test-modal__result-field">
                    <div className="kuika-ai-agent-test-modal__result-field-label">Expected Output</div>
                    <div className="kuika-ai-agent-test-modal__result-field-value">{task.expected_output}</div>
                  </div>
                  <div className="kuika-ai-agent-test-modal__result-field">
                    <div className="kuika-ai-agent-test-modal__result-field-label">Output</div>
                    <div className="kuika-ai-agent-test-modal__result-field-value kuika-ai-agent-test-modal__result-output">
                      {task.output}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {testResults.json_dict && (
          <div className="kuika-ai-agent-test-modal__final-result">
            <div className="kuika-ai-agent-test-modal__result-field-label">Final Result</div>
            <pre className="kuika-ai-agent-test-modal__result-json">
              {JSON.stringify(testResults.json_dict, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  render(): ReactNode {
    const { showResults, testStatus } = this.state;
    const { inputs, agents } = this.props;
    const hasNoAgents = !agents || agents.length === 0;

    // Same commit slot, two identities depending on which screen of the modal is showing: the
    // results screen goes "back" (no loading/disabled — it's a local state flip), the input
    // screen "Test"s (which carries the async loading/disabled rules).
    let submitLabel: string;
    let onSubmitClick: () => void;
    let submitStatus: KuikaButtonStatus | undefined;
    let submitDisabled: boolean;
    if (showResults) {
      submitLabel = "Back to inputs";
      onSubmitClick = () => {
        this.setState({ showResults: false, testResults: undefined });
      };
      submitStatus = undefined;
      submitDisabled = false;
    } else {
      submitLabel = "Test";
      onSubmitClick = () => {
        this.formRef.current?.submit();
      };
      submitStatus = testStatus;
      submitDisabled = hasNoAgents;
    }

    return (
      <Form ref={this.formRef} onFinish={this.onTest} onFieldsChange={this.handleFormChange}>
        <KModal
          className="kuika-ai-agent-test-modal"
          header="Test Agent"
          showModal={this.props.isOpen}
          closeModal={this.closeModal}
          width={showResults ? 1000 : 720}
          isLoading={this.state.isLoading}
          footer={
            <KModalFooter
              submitLabel={submitLabel}
              onSubmit={onSubmitClick}
              status={submitStatus}
              statusLabels={{ loading: "Testing", success: "Done" }}
              disabled={submitDisabled}
            />
          }
        >
          <div className="kuika-ai-agent-test-modal__container">
            {!showResults ? this.renderInputsForm() : this.renderResults()}
          </div>
        </KModal>
      </Form>
    );
  }
}
